import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Pagination from "../../components/admin/Pagination";
import TableScroll from "../../components/TableScroll";
import {
  fetchUserById,
  processAccountDeletion,
  toggleUserStatus,
  updateUserCapabilities,
  verifyUser,
  approveVendor,
  rejectVendor,
} from "../../services/userApi";
import { fetchOrders } from "../../services/orderApi";
import { openStorageUrl } from "../../services/storageApi";
import "../../styles/admin-common.css";

const PAGE_SIZE = 10;

const formatCurrency = (value) =>
  `₹${Number(value ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const formatDate = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-";

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sellerOrders, setSellerOrders] = useState([]);
  const [vendorOrders, setVendorOrders] = useState([]);
  const [sellerPage, setSellerPage] = useState(0);
  const [vendorPage, setVendorPage] = useState(0);
  const [sellerPages, setSellerPages] = useState(1);
  const [vendorPages, setVendorPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [sellerEnabled, setSellerEnabled] = useState(false);
  const [buyerEnabled, setBuyerEnabled] = useState(false);

  const loadUser = () => {
    setLoading(true);
    setError("");
    fetchUserById(userId)
      .then((data) => {
        setUser(data);
        setSellerEnabled(Boolean(data.sellerEnabled));
        setBuyerEnabled(Boolean(data.buyerEnabled));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUser();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    setOrdersLoading(true);
    Promise.all([
      fetchOrders({ page: sellerPage, size: PAGE_SIZE, userId }),
      fetchOrders({ page: vendorPage, size: PAGE_SIZE, buyerId: userId }),
    ])
      .then(([sellerData, vendorData]) => {
        setSellerOrders(sellerData.content || []);
        setSellerPages(sellerData.pagination?.totalPages || 1);
        setVendorOrders(vendorData.content || []);
        setVendorPages(vendorData.pagination?.totalPages || 1);
      })
      .catch((err) => setError(err.message))
      .finally(() => setOrdersLoading(false));
  }, [user, userId, sellerPage, vendorPage]);

  const runAction = async (action) => {
    setActionLoading(true);
    setError("");
    try {
      await action();
      loadUser();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="admin-page admin-loading">Loading user</div>;

  if (error && !user) {
    return (
      <div className="admin-page">
        <p className="error-msg">{error}</p>
        <button type="button" className="admin-btn secondary" onClick={() => navigate(-1)}>Back</button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="admin-page">
        <p>User not found.</p>
        <button type="button" className="admin-btn secondary" onClick={() => navigate("/users")}>Back</button>
      </div>
    );
  }

  const isAdmin = user.role === "ADMIN";
  const capabilitiesDirty =
    sellerEnabled !== Boolean(user.sellerEnabled) ||
    buyerEnabled !== Boolean(user.buyerEnabled);

  return (
    <div className="admin-page">
      <button type="button" className="admin-btn secondary" style={{ marginBottom: 16 }} onClick={() => navigate(-1)}>
        ← Back
      </button>

      <h2>{user.name || user.email || `User #${user.id}`}</h2>
      <p className="subtitle">
        {user.role} · Joined {formatDate(user.createdAt)}
        {user.deletedAt && " · Anonymized"}
        {user.deletionRequestedAt && !user.deletedAt && " · Deletion requested"}
      </p>

      {error && <p className="error-msg">{error}</p>}

      <div className="details-grid">
        <div className="admin-card details-section">
          <h3>Contact</h3>
          <p><strong>Email:</strong> {user.email || "-"}</p>
          <p><strong>Phone:</strong> {user.phoneNumber || "-"}</p>
          {user.upiId && <p><strong>UPI:</strong> {user.upiId}</p>}
          <p><strong>Verified:</strong> {user.isVerified ? "Yes" : "No"}</p>
          <p><strong>Active:</strong> {user.isActive !== false ? "Yes" : "No"}</p>
          <p><strong>Last updated:</strong> {formatDate(user.updatedAt)}</p>
        </div>

        <div className="admin-card details-section">
          <h3>Access</h3>
          {!isAdmin ? (
            <>
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={sellerEnabled}
                  disabled={actionLoading}
                  onChange={(e) => setSellerEnabled(e.target.checked)}
                />
                Seller (can list devices)
              </label>
              <label className="checkbox-field" style={{ marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={buyerEnabled}
                  disabled={actionLoading}
                  onChange={(e) => setBuyerEnabled(e.target.checked)}
                />
                Vendor (can inspect orders)
              </label>
              <div className="form-actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="admin-btn"
                  disabled={actionLoading || !capabilitiesDirty}
                  onClick={() =>
                    runAction(() =>
                      updateUserCapabilities(user.id, { sellerEnabled, buyerEnabled }).then(setUser)
                    )
                  }
                >
                  Save access
                </button>
              </div>
            </>
          ) : (
            <p className="muted-text">Admin account — capabilities not editable here.</p>
          )}
        </div>

        {user.wallet && (
          <div className="admin-card details-section">
            <h3>Wallet</h3>
            <p><strong>Balance:</strong> {formatCurrency(user.wallet.balance)}</p>
            <p><strong>On hold:</strong> {formatCurrency(user.wallet.holdAmount)}</p>
            <p><strong>Available:</strong> {formatCurrency(user.wallet.availableBalance)}</p>
          </div>
        )}

        {(user.buyerEnabled || user.role === "BUYER") && (
          <div className="admin-card details-section">
            <h3>Vendor onboarding</h3>
            <p><strong>Status:</strong> {user.vendorApprovalStatus || "NONE"}</p>
            <p><strong>Name:</strong> {[user.firstName, user.lastName].filter(Boolean).join(" ") || user.name || "-"}</p>
            {user.vendorLivePhotoUrl && (
              <p>
                <strong>Live photo:</strong>{" "}
                <button
                  type="button"
                  className="admin-btn link"
                  onClick={() => openStorageUrl(user.vendorLivePhotoUrl)}
                >
                  View photo
                </button>
              </p>
            )}
            {user.vendorAgreementPdfUrl && (
              <p>
                <strong>Agreement PDF:</strong>{" "}
                <button
                  type="button"
                  className="admin-btn link"
                  onClick={() => openStorageUrl(`${user.vendorAgreementPdfUrl}${user.vendorAgreementPdfUrl.includes("?") ? "&" : "?"}t=${Date.now()}`)}
                >
                  Open PDF
                </button>
              </p>
            )}
            {(user.vendorApprovalStatus === "PENDING"
              || user.vendorApprovalStatus === "REJECTED"
              || user.vendorApprovalStatus === "APPROVED") && (
              <div className="form-actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="admin-btn"
                  disabled={actionLoading}
                  onClick={() => runAction(() => approveVendor(user.id).then(setUser))}
                >
                  {user.vendorApprovalStatus === "APPROVED"
                    ? "Re-stamp company signature"
                    : "Approve vendor"}
                </button>
                {user.vendorApprovalStatus === "PENDING" && (
                  <button
                    type="button"
                    className="admin-btn secondary"
                    disabled={actionLoading}
                    onClick={() => runAction(() => rejectVendor(user.id).then(setUser))}
                  >
                    Reject
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {!isAdmin && (
        <div className="admin-card">
          <h3>Admin actions</h3>
          <div className="form-actions">
            {!user.isVerified && (
              <button
                type="button"
                className="admin-btn"
                disabled={actionLoading}
                onClick={() => runAction(() => verifyUser(user.id))}
              >
                Verify account
              </button>
            )}
            <button
              type="button"
              className="admin-btn secondary"
              disabled={actionLoading}
              onClick={() => runAction(() => toggleUserStatus(user.id))}
            >
              Toggle active
            </button>
            {user.deletionRequestedAt && !user.deletedAt && (
              <button
                type="button"
                className="admin-btn danger"
                disabled={actionLoading}
                onClick={() => runAction(() => processAccountDeletion(user.id))}
              >
                Process deletion
              </button>
            )}
          </div>
        </div>
      )}

      <UserOrderSection
        title="Orders as customer (seller)"
        orders={sellerOrders}
        loading={ordersLoading}
        page={sellerPage}
        totalPages={sellerPages}
        onPageChange={setSellerPage}
        emptyMessage="No orders placed by this user."
        navigate={navigate}
      />

      <UserOrderSection
        title="Orders as vendor (buyer)"
        orders={vendorOrders}
        loading={ordersLoading}
        page={vendorPage}
        totalPages={vendorPages}
        onPageChange={setVendorPage}
        emptyMessage="No orders assigned to this vendor."
        navigate={navigate}
      />
    </div>
  );
};

const UserOrderSection = ({
  title,
  orders,
  loading,
  page,
  totalPages,
  onPageChange,
  emptyMessage,
  navigate,
}) => (
  <div className="admin-card" style={{ marginTop: 20 }}>
    <h3>{title}</h3>
    {loading ? (
      <div className="admin-loading">Loading orders</div>
    ) : orders.length === 0 ? (
      <p className="empty-msg">{emptyMessage}</p>
    ) : (
      <>
        <TableScroll>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Status</th>
                <th>Device</th>
                <th>Quoted</th>
                <th>Final</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td><strong>{order.orderNumber}</strong></td>
                  <td>
                    <span className={`status-badge status-${order.status}`}>
                      {order.status?.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td>{order.brandName} {order.deviceModelName}</td>
                  <td>{formatCurrency(order.quotedPrice)}</td>
                  <td>{order.finalPrice != null ? formatCurrency(order.finalPrice) : "-"}</td>
                  <td>{formatDate(order.createdAt)}</td>
                  <td>
                    <button type="button" className="admin-btn secondary" onClick={() => navigate(`/order/${order.id}`)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
      </>
    )}
  </div>
);

export default UserDetails;
